import {
    PortfolioAddServiceProductParameters,
    PortfolioEnterpriseProductPriceAdjustment,
} from '@mews-commander/data-access';
import type { ServiceKeyIdPair, ServiceWithId, ServiceDataTransformationProps } from './types';

export const createServiceNameToIdMap = (serviceKeyIdPairs: ServiceKeyIdPair[]): Record<string, string> => {
    return serviceKeyIdPairs.reduce((acc, pair) => {
        const [key] = Object.keys(pair);
        acc[key] = pair[key];
        return acc;
    }, {});
};

export const transformServicesWithIds = (params: ServiceDataTransformationProps): ServiceWithId[] => {
    const { services, serviceKeyIdPairs } = params;
    const serviceNameToIdMap = createServiceNameToIdMap(serviceKeyIdPairs);

    return Object.entries(services || {}).map(([serviceName, serviceData]) => {
        const serviceId = serviceNameToIdMap[serviceName];

        return {
            serviceId,
            ...serviceData,
            consumptionMoment: serviceData.consumptionMoment === '' ? null : serviceData.consumptionMoment,
            options: serviceData.options || [],
            promotions: serviceData.promotions || [],
        };
    });
};

export const createPriceAdjustment = (
    service: ServiceWithId
): PortfolioAddServiceProductParameters['priceAdjustment'] => {
    const { fixedPriceAdjustments, percentagePriceAdjustments } = service;

    if (fixedPriceAdjustments && Object.keys(fixedPriceAdjustments).length > 0) {
        const adjustmentPropertyId = Object.keys(fixedPriceAdjustments)[0];
        const adjustmentData = fixedPriceAdjustments[adjustmentPropertyId];

        return {
            discriminator: PortfolioEnterpriseProductPriceAdjustment.Fixed,
            value: {
                currencyCode: adjustmentData.currencyCode,
                value: adjustmentData.value,
            },
        };
    }

    if (percentagePriceAdjustments && Object.keys(percentagePriceAdjustments).length > 0) {
        const adjustmentPropertyId = Object.keys(percentagePriceAdjustments)[0];
        const adjustmentData = percentagePriceAdjustments[adjustmentPropertyId];

        return {
            discriminator: PortfolioEnterpriseProductPriceAdjustment.Percentage,
            value: {
                amountPart: adjustmentData.amountPart,
                multiplier: adjustmentData.multiplier,
            },
        };
    }

    return undefined;
};

export const transformToServiceProducts = (
    servicesWithIds: ServiceWithId[]
): PortfolioAddServiceProductParameters[] => {
    return servicesWithIds.map((service) => {
        const { fixedPriceAdjustments, percentagePriceAdjustments, ...rest } = service;

        const baseObject: Omit<PortfolioAddServiceProductParameters, 'priceAdjustment'> = {
            serviceId: rest.serviceId,
            charging: rest.charging,
            consumptionMoment: rest.consumptionMoment || null,
            options: rest.options || [],
            promotions: rest.promotions || [],
        };

        const priceAdjustment = createPriceAdjustment(service);

        return {
            ...baseObject,
            ...(priceAdjustment && { priceAdjustment }),
        } as PortfolioAddServiceProductParameters;
    });
};

export const createServiceOption = (key: string) => ({
    label: getDisplayNameFromKey(key),
    value: key,
    disabled: false,
    secondaryText: null,
    indent: false,
    inactive: false,
});

/**
 * Creates a unique service key by appending service ID when duplicates exist.
 *
 * This function handles the case where multiple services have the same localized name.
 * It keeps the display name clean for users but creates unique internal keys by appending
 * the service ID when duplicates are detected.
 *
 * Examples:
 * - First "Room Service" -> "Room Service"
 * - Second "Room Service" -> "Room Service__service-id-123"
 * - Third "Room Service" -> "Room Service__service-id-456"
 *
 * @param baseKey - The base service key (localized service name)
 * @param serviceId - The unique service ID to append if needed
 * @param existingKeys - Set of already existing keys to check against
 * @returns A unique service key that doesn't conflict with existing keys
 */
export const createUniqueServiceKey = (baseKey: string, serviceId: string, existingKeys: Set<string>): string => {
    let uniqueKey = baseKey;

    if (existingKeys.has(uniqueKey)) {
        // Create a unique key by appending service ID, but keep it hidden from users
        uniqueKey = `${baseKey}__${serviceId}`;
    }

    return uniqueKey;
};

/**
 * Extracts the clean display name from an internal service key.
 *
 * This function removes any service ID suffixes that were added for uniqueness
 * to provide a clean display name for users.
 *
 * @param internalKey - The internal key that may contain service ID suffix
 * @returns The clean display name without any internal suffixes
 */
export const getDisplayNameFromKey = (internalKey: string): string => {
    // Remove the service ID suffix if it exists (format: "name__serviceId")
    const separatorIndex = internalKey.lastIndexOf('__');
    if (separatorIndex !== -1) {
        return internalKey.substring(0, separatorIndex);
    }
    return internalKey;
};
